/**
 * Granskning of a beta contract (ADR-0102 addendum 2 / 3) — one screen
 * across every document of the contract (main + template bilagor), one
 * approval as today:
 *
 *   - Uppgifter: field requests and field comments per document, with
 *     "Värdet ändrat sedan begäran: old → new", close / reopen and
 *     "Gå till fältet" (returns to draft after a confirm).
 *   - Text: one document at a time with its suggestions (red / green),
 *     accept / reject, comments; text edits in in_review become suggestions.
 *     Anyone who can view the contract may review (ADR-0102 addendum 4);
 *     deciding and commenting work in draft and pending too.
 *     "Visa malländringar" colours the author's adaptations violet.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { describeCompositionForm, type BlockRegistry } from '@hyranai/regta-document-ir';
import { createRegtaBlockRegistry, getComposition, hasComposition } from '@hyranai/regta-blocks';
import { ApiError, tenantApi, type ContractAttachment, type ContractDetail, type ReviewThread } from '../../../api.js';
import { useTenantSession } from '../../../auth-context.js';
import { useInvalidate, useTenantQuery } from '../../../query.js';
import { usePageTitle } from '../../../use-page-title.js';
import { StatusChip } from '../../../components/ContractStatusBar.js';
import { ReviewPanel } from '../../../components/ReviewPanel.js';
import { AdaptTextPanel } from '../finetune/AdaptTextPanel.js';
import { EDITOR_AVTAL_WIDTH } from '../../../lib/editor-layout.js';
import { useFieldActions, type FieldTarget } from '../../../components/beta-review/FieldActions.js';
import { ReviewThreadCard, useUserNames } from '../../../components/beta-review/ReviewThreads.js';
import { useReviewItems } from '../../../components/beta-review/useReviewItems.js';
import {
  MAIN_DOCUMENT,
  attachmentIdOf,
  documentKey,
  fieldHref,
  fieldRequestsLabel,
  groupThreadsByDocument,
  signingGateText,
  suggestionsLabel,
  type ReviewView,
} from '../../../components/beta-review/review-model.js';

let blockRegistry: BlockRegistry | null = null;
const registry = (): BlockRegistry => (blockRegistry ??= createRegtaBlockRegistry());

function fieldLabels(templateKey: string | null, templateVersion: string | null, values: Record<string, unknown>): Map<string, string> {
  if (!templateKey || !templateVersion) return new Map();
  try {
    const composition = getComposition(templateKey, templateVersion, values);
    if (!composition) return new Map();
    return new Map(describeCompositionForm(composition, registry()).fields.map((f) => [f.key, f.label]));
  } catch {
    return new Map();
  }
}

const isIrBilaga = (a: ContractAttachment): boolean =>
  a.kind === 'template' && !!a.templateKey && !!a.templateVersion && hasComposition(a.templateKey, a.templateVersion);

export function BetaReviewPage() {
  const { id } = useParams<{ id: string }>();
  const { token, actor } = useTenantSession();
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const [showResolved, setShowResolved] = useState(false);

  const contractQ = useTenantQuery(
    'contracts.get',
    (cid) => tenantApi.contracts.get(cid as string),
    id ? [id] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const reviewsQ = useTenantQuery(
    'reviews.list_for_contract',
    (cid) => tenantApi.reviews.list_for_contract({ contract_id: cid as string }),
    id ? [id] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const attachmentsQ = useTenantQuery(
    'contract_attachments.list',
    (cid) => tenantApi.contract_attachments.list({ contract_id: cid as string }),
    id ? [id] : [],
    { ignoreActiveGroup: true, revalidateOnFocus: false },
  );
  const contract: ContractDetail | null = contractQ.data?.contract ?? null;
  const attachments = useMemo(() => attachmentsQ.data?.attachments ?? [], [attachmentsQ.data]);
  const bilagor = useMemo(() => attachments.filter(isIrBilaga), [attachments]);
  const items = useReviewItems(
    contract?.documentEngine === 'ir' ? contract.id : null,
    bilagor.map((a) => a.id),
    contract ? `${contract.updatedAt}|${contract.status}` : '',
  );
  const names = useUserNames();

  const reviews = reviewsQ.data?.reviews ?? [];
  const activeReview = reviews.find((r) => r.status === 'pending') ?? null;

  const view: ReviewView = search.get('vy') === 'text' ? 'text' : 'uppgifter';
  const requestedDoc = search.get('dokument');
  const docScope = bilagor.some((a) => a.id === requestedDoc) ? requestedDoc : null;
  const setParam = (key: string, value: string | null) =>
    setSearch(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const documentTitle = useCallback(
    (attachmentId: string | null): string => {
      if (!attachmentId) return 'Huvudavtal';
      const a = attachments.find((x) => x.id === attachmentId);
      if (!a) return 'Bilaga';
      return `${a.number != null ? `Bilaga ${a.number}` : 'Bilaga'} – ${a.displayName}`;
    },
    [attachments],
  );
  const labels = useMemo(() => {
    const out = new Map<string, Map<string, string>>();
    if (!contract) return out;
    out.set(MAIN_DOCUMENT, fieldLabels(contract.templateKey, contract.templateVersion, contract.values ?? {}));
    for (const a of bilagor) out.set(a.id, fieldLabels(a.templateKey, a.templateVersion, (a.values ?? {}) as Record<string, unknown>));
    return out;
  }, [contract, bilagor]);
  const fieldLabel = useCallback(
    (t: FieldTarget) => labels.get(documentKey(t.attachmentId))?.get(t.key) ?? null,
    [labels],
  );

  const fieldActions = useFieldActions({
    contract,
    fieldLabel,
    onOpenField: (t) => contract && navigate(fieldHref(contract.id, t.key, t.attachmentId)),
    onCommented: items.refresh,
  });

  usePageTitle(contract ? `Granskning – ${contract.title || 'Avtal'}` : null);

  const onTextChanged = useCallback(() => {
    if (!contract) return;
    void Promise.all([
      items.refresh(),
      invalidate('contracts.get', contract.id),
      invalidate('reviews.list_for_contract', contract.id),
    ]);
  }, [contract, invalidate, items]);

  if (contractQ.error instanceof ApiError && contractQ.error.status === 404) {
    return (
      <div className="rounded border border-stone-200 bg-white p-4 text-sm text-stone-600">
        Avtalet finns inte längre.{' '}
        <Link to="/contracts" className="font-medium text-stone-900 underline">
          Till avtalslistan
        </Link>
      </div>
    );
  }
  if (contractQ.error && !contract) {
    return <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-700">{(contractQ.error as Error).message}</div>;
  }
  if (!contract || !token) return <div className="text-sm text-stone-400">Laddar…</div>;
  if (contract.documentEngine !== 'ir') return <Navigate to={`/contracts/${contract.id}/edit?tab=granskning`} replace />;

  const inReview = contract.status === 'in_review';
  const fieldThreads = items.threads.filter((t) => t.anchor_kind === 'field' && (showResolved || !t.resolved));
  const fieldGroups = groupThreadsByDocument(fieldThreads, [MAIN_DOCUMENT, ...bilagor.map((a) => a.id)]);
  const openByDoc = (key: string) => (items.suggestions.get(key) ?? []).filter((s) => s.open).length;
  const gate = signingGateText(items);
  const documents = [{ id: null as string | null }, ...bilagor.map((a) => ({ id: a.id as string | null }))];

  const tab = (key: ReviewView, label: string, count: number) => (
    <button
      key={key}
      type="button"
      role="tab"
      aria-selected={view === key}
      onClick={() => setParam('vy', key === 'uppgifter' ? null : key)}
      className={
        'border-b-2 px-3 py-1.5 text-xs font-medium tracking-wider uppercase transition-colors ' +
        (view === key ? 'border-stone-900 text-stone-900' : 'border-transparent text-stone-500 hover:text-stone-700')
      }
    >
      {label} {count > 0 && <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-[10px] text-amber-900">{count}</span>}
    </button>
  );

  return (
    <div className={EDITOR_AVTAL_WIDTH + ' flex h-full flex-col'}>
      {fieldActions.element}
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <Link
          to={`/contracts/${contract.id}/beta`}
          className="inline-flex items-center gap-1 text-xs text-stone-500 transition-colors hover:text-stone-800 hover:underline"
        >
          ← Tillbaka till avtalet
        </Link>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold text-stone-900">Granskning</h1>
        <span className="text-sm text-stone-500">· {contract.title || 'Avtal'}</span>
        <StatusChip status={contract.status} reviewed={contract.status === 'pending' && !!contract.approval?.approved} />
      </div>

      {actor && (activeReview || reviews.length > 0) && (
        <div className="mb-3 max-w-2xl">
          <ReviewPanel token={token} contract={contract} actorId={actor.id} />
        </div>
      )}
      {gate && (
        <p className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {gate} Avtalet kan godkännas ändå.
        </p>
      )}
      {fieldActions.error && (
        <p role="alert" className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800">
          {fieldActions.error}{' '}
          <button type="button" className="underline" onClick={fieldActions.clearError}>
            Stäng
          </button>
        </p>
      )}

      <div role="tablist" aria-label="Granskning" className="mb-3 flex items-center gap-1 border-b border-stone-200">
        {tab('uppgifter', 'Uppgifter', items.openFieldRequests)}
        {tab('text', 'Text', items.openSuggestions)}
      </div>

      {view === 'uppgifter' ? (
        <main className="flex max-w-3xl flex-col gap-4 pb-8 text-xs">
          <div className="flex flex-wrap items-center gap-3 text-stone-600">
            <p className="flex-1">
              Begär ändringar av uppgifter genom att klicka på ett gult värde i förhandsvisningen eller i texten och välja
              Kommentera. En begäran stängs här – att ändra värdet stänger den inte.
            </p>
            <label className="inline-flex cursor-pointer items-center gap-1.5 select-none">
              <input type="checkbox" className="accent-stone-900" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} />
              Visa stängda
            </label>
          </div>
          {fieldGroups.length === 0 ? (
            <p className="text-stone-500">{showResolved ? 'Inga begäranden om uppgifter.' : 'Inga öppna begäranden om uppgifter.'}</p>
          ) : (
            fieldGroups.map((g) => (
              <section key={g.documentKey}>
                <h2 className="mb-2 text-[11px] font-semibold tracking-wider text-stone-500 uppercase">
                  {documentTitle(attachmentIdOf(g.documentKey))}
                </h2>
                <ul className="flex flex-col gap-2">
                  {g.threads.map((t: ReviewThread) => {
                    const target = { key: t.anchor ?? '', attachmentId: t.attachment_id };
                    return (
                      <ReviewThreadCard
                        key={t.id}
                        thread={t}
                        contractId={contract.id}
                        names={names}
                        writable={items.writable}
                        heading={fieldLabel(target) ?? t.anchor ?? 'Uppgift'}
                        onGoToField={t.anchor ? () => void fieldActions.changeInForm(target) : undefined}
                        onChanged={items.refresh}
                      />
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </main>
      ) : (
        <>
          {documents.length > 1 && (
            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs" role="tablist" aria-label="Dokument">
              <span className="text-stone-500">Dokument:</span>
              {documents.map((d) => {
                const open = openByDoc(documentKey(d.id));
                return (
                  <button
                    key={d.id ?? 'main'}
                    type="button"
                    role="tab"
                    aria-selected={docScope === d.id}
                    onClick={() => setParam('dokument', d.id)}
                    className={
                      'rounded border px-2 py-1 transition-colors ' +
                      (docScope === d.id ? 'border-stone-800 bg-stone-800 text-white' : 'border-stone-300 bg-white text-stone-700 hover:bg-stone-50')
                    }
                  >
                    {documentTitle(d.id)}
                    {open > 0 && <span className="ml-1.5 rounded-full bg-red-100 px-1.5 text-[10px] text-red-800">{open}</span>}
                  </button>
                );
              })}
            </div>
          )}
          {items.openSuggestions > 0 && documents.length > 1 && (
            <p className="mb-2 text-[11px] text-stone-500">
              Öppna förslag i avtalet: {suggestionsLabel(items.openSuggestions)}
              {items.openFieldRequests > 0 ? ` · ${fieldRequestsLabel(items.openFieldRequests)}` : ''}
            </p>
          )}
          <main className="flex min-h-[75vh] flex-1 flex-col overflow-hidden border border-stone-200 bg-white">
            <AdaptTextPanel
              key={docScope ?? 'main'}
              mode="review"
              contractId={contract.id}
              attachmentId={docScope}
              refreshKey={contract.status}
              actorId={actor?.id ?? null}
              allowEdit={inReview}
              onChanged={onTextChanged}
              onValueClick={(key, rect) => fieldActions.open({ key, attachmentId: docScope }, rect)}
            />
          </main>
        </>
      )}
    </div>
  );
}
